// ============================================================================
// SignalCard — redesigned trade-signal card (slice 4 of the redesign).
//
// Visual structure (per design-handoff/screens.jsx::SignalCard):
//   • top accent strip (green LONG / red SHORT)
//   • header: ticker + DirBadge / price + today's %
//   • confidence row: VALIDATING / VALIDATED chip + bar + big % number
//   • EV badge (when a calibration bucket exists)
//   • 3-up tile grid (Target / Stop / R:R)
//   • secondary line: Score · Swing · BT win% (BT win% ADMIN-ONLY)
//   • reason chips (top 3 from signal.reasons)
//   • optional options strip (when signal.option present)
//   • full-width direction-coloured Trade CTA (verifies live price first)
//   • AI insight footer (when aiInsight present) — preserved from the
//     previous implementation
//
// What's unchanged from before:
//   – Props: signal, onClick, onTrade, cs, livePrice, aiInsight, aiLoading
//   – Live-price verification before navigating to Trade
//   – Dual stock + option trade flows
//   – memo() with isEqualSignalProps so the grid doesn't re-render all 50
//     cards every time one of them flips from VALIDATING → VALIDATED.
//
// New: BT win% is gated on `useAuth().user?.role === 'admin'` per the
// critical business rule (design_handoff/README.md). Non-admins never see
// realised win-rate or backtest numbers on these cards.
// ============================================================================

import { useState, memo } from 'react';
import { Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getQuote } from '../utils/marketEngine';
import { getOptionContractQuote } from '../utils/polygonService';
import { formatDateOnly } from '../utils/format';
import { TERMS } from '../utils/terms';
import { buildConfidenceTitle } from '../utils/confidenceBreakdown';
import Icon from './ui/Icon';
import DirBadge from './ui/DirBadge';
import ConfBar from './ui/ConfBar';
import { confColor } from './ui/ConfRing';

function SignalCardImpl({ signal, onClick, onTrade, cs = '$', livePrice, aiInsight, aiLoading }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const isLong  = signal.direction === 'LONG';
  const dirGrad = isLong ? 'var(--grad-success)' : 'var(--grad-danger)';
  const dirGlow = isLong ? 'var(--green-glow)' : 'var(--red-glow)';
  // livePrice is a fresher quote fetched separately; prefer it over the scan price.
  const displayPrice = livePrice ?? signal.currentPrice;

  const [stockValidating,  setStockValidating]  = useState(false);
  const [stockError,       setStockError]       = useState(null);
  const [optionValidating, setOptionValidating] = useState(false);
  const [optionError,      setOptionError]      = useState(null);

  const conf       = Math.round(Number(signal.confidence) || 0);
  const validating = signal._confidenceSource === 'pending';
  const validated  = signal._confidenceSource === 'backtest';
  const target     = Number(signal.targetPrice);
  const stop       = Number(signal.stopLoss);
  const rr         = Number(signal.riskReward) || (signal.targetPct && signal.stopPct ? Math.abs(signal.targetPct / signal.stopPct) : null);
  const score      = Math.abs(Number(signal.score) || 0);
  const swing      = signal.holdingDays || signal.days || (signal.timeframe?.match(/\d+/)?.[0]) || 5;
  const change     = Number(signal.changePct) || 0;
  const ev         = Number.isFinite(signal.evPct) ? Number(signal.evPct) : null;
  const btWin      = signal._backtestResult?.winRate ?? null;
  const reasons    = signal.reasons || [];

  // ── Trade handlers (preserved from previous impl) ─────────────────────────
  const handleStockTrade = async (e) => {
    e.stopPropagation();
    if (!onTrade || stockValidating) return;
    setStockValidating(true); setStockError(null);
    try {
      const q = await getQuote(signal.ticker);
      if (!q?.price) throw new Error('No quote returned');
      onTrade({
        ticker: signal.ticker, direction: isLong ? 'BUY' : 'SHORT',
        price: q.price, type: 'stock', notes: reasons.join(', '),
        signalSource: `AI Signal · ${signal.timeframe ?? ''} · ${signal.dataSource ?? ''}`.replace(/\s·\s$/, ''),
        stopPrice:   signal.stopLoss   ?? null,
        targetPrice: signal.targetPrice ?? null,
        swingDays: signal.days || signal.holdingDays || null,
        // For Kelly-fractional sizing in NewTradeModal — uses the signal's
        // validated win-rate proxy (confidence) and reward-to-risk to set
        // an edge-proportional position size instead of a flat 0.75%.
        confidence:  signal.confidence,
        riskReward:  signal.riskReward,
      });
    } catch (err) {
      const rate = err.message?.includes('Rate limited') || err.message?.includes('429');
      setStockError(rate ? 'Rate limited — try again in a moment' : 'Could not verify live price');
    }
    setStockValidating(false);
  };

  const handleOptionTrade = async (e) => {
    e.stopPropagation();
    if (!onTrade || !signal.option || optionValidating) return;
    setOptionValidating(true); setOptionError(null);
    try {
      const q = await getQuote(signal.ticker);
      if (!q?.price) throw new Error('No stock quote returned');
      let premium = signal.option.premium;
      if (signal.option._source === 'polygon' && signal.option.symbol) {
        try {
          const optQ = await getOptionContractQuote(signal.option.symbol);
          if (optQ?.midpoint != null) premium = optQ.midpoint;
        } catch { /* fall through to scan premium */ }
      }
      onTrade({
        ticker: signal.ticker, direction: isLong ? 'BUY' : 'SHORT',
        price: premium, type: 'option',
        optionType:   signal.option.type,
        optionStrike: signal.option.strike,
        optionExpiry: signal.option.expiration,
        optionContractSymbol: signal.option._source === 'polygon' ? signal.option.symbol : null,
        notes: signal.option.strategy,
        signalSource: `AI Signal · ${signal.timeframe ?? ''} · ${signal.dataSource ?? ''}`.replace(/\s·\s$/, ''),
        // Standard premium-buyer practice: −50% premium = stop, +100% = target.
        // Mirrors the existing exit-rule thresholds in signalHealth.js.
        stopPrice:   Number.isFinite(premium) && premium > 0 ? +(premium * 0.5).toFixed(4) : null,
        targetPrice: Number.isFinite(premium) && premium > 0 ? +(premium * 2.0).toFixed(4) : null,
        swingDays: signal.days || signal.holdingDays || null,
        // Kelly inputs — option R:R is fixed at 2:1 by the −50%/+100%
        // convention above, so the modal can size on the underlying's
        // win-rate proxy alone.
        confidence:  signal.confidence,
        riskReward:  2,
      });
    } catch (err) {
      const rate = err.message?.includes('Rate limited') || err.message?.includes('429');
      setOptionError(rate ? 'Rate limited — try again in a moment' : 'Could not verify option price');
    }
    setOptionValidating(false);
  };

  return (
    <article
      onClick={onClick}
      className="card card-hover"
      style={{
        padding: 'var(--card-pad)', position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* top accent strip — direction at a glance */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: dirGrad, opacity: 0.85,
      }} />

      {/* header: ticker + DirBadge / price + today's % */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 10, marginBottom: 14,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 19, fontWeight: 800, letterSpacing: '-0.03em',
              color: 'var(--text-primary)',
            }}>{signal.ticker}</span>
            <DirBadge dir={signal.direction} size="sm" />
          </div>
          <div style={{
            fontSize: 11, color: 'var(--text-muted)', marginTop: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 220,
          }}>
            {signal.name || signal.ticker}
            {signal.primaryReasonCluster && <> · <span style={{ textTransform: 'capitalize' }}>{signal.primaryReasonCluster}</span></>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
            {cs}{Number(displayPrice || 0).toFixed(2)}
          </div>
          <div className="mono" style={{
            fontSize: 11, fontWeight: 700,
            color: change >= 0 ? 'var(--green)' : 'var(--red)',
          }}>{change >= 0 ? '+' : ''}{change.toFixed(2)}%</div>
        </div>
      </div>

      {/* confidence row */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 6, gap: 6,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span className="eyebrow" title={TERMS.confidence} style={{ cursor: 'help' }}>Confidence</span>
            {validating ? (
              <span title={TERMS.validating} style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 10, fontWeight: 700, padding: '3px 8px',
                borderRadius: 'var(--r-pill)',
                background: 'var(--accent-dim)', color: 'var(--accent-soft)',
                border: '1px solid var(--border-strong)',
                cursor: 'help',
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: 'var(--accent-soft)',
                  animation: 'pulseSoft 1.2s var(--ease-in-out) infinite',
                }} />
                VALIDATING
              </span>
            ) : validated ? (
              <span
                title={signal._backtestResult
                  ? `Backtest: ${signal._backtestResult.wins}W / ${signal._backtestResult.losses}L over ${signal._backtestResult.trials} trials · WR ${signal._backtestResult.winRate}% · Sharpe ${signal._backtestResult.sharpe}`
                  : 'Backtested against the last 90 days of real price history.'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 10, fontWeight: 700, padding: '3px 8px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--green-dim)', color: 'var(--green-soft)',
                  border: '1px solid rgba(34,197,94,0.3)',
                }}>
                <Icon name="check" size={11} stroke={2.4} /> VALIDATED
                {signal._backtestResult?.trials != null && (
                  <span style={{ opacity: 0.8, fontWeight: 600 }}>
                    · {signal._backtestResult.trials}t
                  </span>
                )}
              </span>
            ) : null}
          </span>
          <span
            className="mono"
            title={buildConfidenceTitle(signal)}
            style={{
              fontSize: 19, fontWeight: 800, color: confColor(conf),
              cursor: 'help',
            }}
          >
            {conf}<span style={{ fontSize: 11, opacity: 0.7 }}>%</span>
          </span>
        </div>
        <ConfBar value={conf} w="100%" />
        {ev != null && (
          <div style={{ marginTop: 8 }}>
            <span title={TERMS.evShort} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 700, padding: '3px 8px',
              borderRadius: 'var(--r-pill)',
              background: ev >= 0 ? 'var(--green-dim)' : 'var(--red-dim)',
              color:      ev >= 0 ? 'var(--green-soft)' : 'var(--red-soft)',
              border:     `1px solid ${ev >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              cursor: 'help',
            }}>
              <Icon name="target" size={11} stroke={2.4} /> EV {ev >= 0 ? '+' : ''}{ev.toFixed(2)}%
            </span>
          </div>
        )}
      </div>

      {/* 3-up tile grid: Target / Stop / R:R */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7,
        marginBottom: 12,
      }}>
        {[
          ['Target', Number.isFinite(target) ? `${cs}${target.toFixed(2)}` : '—',                'var(--green)',         'Price at which the position closes for a win.'],
          ['Stop',   Number.isFinite(stop)   ? `${cs}${stop.toFixed(2)}`   : '—',                'var(--red)',           'Price at which the position closes for a loss. Sized off the daily ATR.'],
          ['R : R',  rr != null ? rr.toFixed(2) : '—',                                            'var(--text-primary)',  (() => {
            if (rr == null) return 'Reward / Risk ratio — undefined for this signal.';
            const breakeven = Math.round(100 / (1 + Number(rr)));
            return `Reward / Risk = ${Number(rr).toFixed(2)}. If target hits you make ${Number(rr).toFixed(2)}× what you'd lose at stop. Breakeven win rate at this R:R is ~${breakeven}% — anything above that is positive expected value.`;
          })()],
        ].map(([l, v, c, tip]) => (
          <div key={l} title={tip} style={{
            padding: '8px 10px', borderRadius: 'var(--r-sm)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
            minWidth: 0, cursor: 'help',
          }}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>{l}</div>
            <div className="mono" style={{
              fontSize: 13, fontWeight: 700, color: c, lineHeight: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{v}</div>
          </div>
        ))}
      </div>

      {/* secondary stats: Score · Swing · BT win (admin only) */}
      <div className="mono" style={{
        display: 'flex', gap: 12, fontSize: 10.5,
        color: 'var(--text-secondary)', marginBottom: 12, flexWrap: 'wrap',
      }}>
        <span title={TERMS.score} style={{ cursor: 'help' }}>
          <span style={{ color: 'var(--text-muted)' }}>Score </span><b>{score}</b>
        </span>
        <span title={TERMS.swing} style={{ cursor: 'help' }}>
          <span style={{ color: 'var(--text-muted)' }}>Swing </span><b>~{swing}d</b>
        </span>
        {validated && isAdmin && btWin != null && (
          <span title={TERMS.winRate} style={{ cursor: 'help' }}>
            <span style={{ color: 'var(--text-muted)' }}>BT win </span>
            <b style={{ color: 'var(--green)' }}>{Math.round(btWin)}%</b>
          </span>
        )}
      </div>

      {/* reason chips */}
      {reasons.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
          {reasons.slice(0, 3).map((t) => (
            <span key={t} style={{
              fontSize: 9.5, fontWeight: 600, padding: '3px 8px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--accent-dim)', color: 'var(--accent-soft)',
            }}>{t}</span>
          ))}
        </div>
      )}

      {/* option strip */}
      {signal.option && (
        <div style={{
          padding: '9px 11px', borderRadius: 'var(--r-md)',
          background: 'var(--accent-dim)',
          border: '1px solid var(--border-strong)',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 10, fontWeight: 700, color: 'var(--accent-soft)',
              letterSpacing: '0.04em',
            }}>
              <Icon name="layers" size={11} /> OPTIONS{signal.option._source === 'polygon' ? ' · REAL' : ''}
            </span>
            <span title={TERMS.premium} className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-soft)', cursor: 'help' }}>
              {cs}{Number(signal.option.premium).toFixed(2)}
            </span>
          </div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
            <span title={TERMS.optionType} style={{ cursor: 'help' }}>Buy {signal.option.type}</span>
            {signal.option.strike != null && (
              <span title={TERMS.strike} style={{ cursor: 'help' }}> · {cs}{Number(signal.option.strike).toFixed(2)}</span>
            )}
            {signal.option.delta != null && (
              <span title={TERMS.delta} style={{ cursor: 'help' }}> · Δ{Number(signal.option.delta).toFixed(2)}</span>
            )}
            {signal.option.expiration && (
              <span title={TERMS.expiry} style={{ cursor: 'help' }}> · exp {formatDateOnly(signal.option.expiration)}</span>
            )}
          </div>

          {/* Options-NATIVE metrics — replaces implying the stock's win-rate
              applies to the option. POP + EV are computed from the IV price
              distribution + delta-theta, so they actually describe the
              option's expected outcome. */}
          {signal.option.evaluation && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8,
              fontSize: 9.5, color: 'var(--text-secondary)',
            }}>
              {signal.option.evaluation.pop != null && (
                <span title="Probability of profit — from the option's own implied-vol price distribution + delta/theta, NOT the stock's win rate.">
                  <span style={{ color: 'var(--text-muted)' }}>POP </span>
                  <b style={{ color: signal.option.evaluation.pop >= 50 ? 'var(--green)' : 'var(--amber)' }}>
                    {signal.option.evaluation.pop}%
                  </b>
                </span>
              )}
              {signal.option.evaluation.evPctOfPremium != null && (
                <span title="Expected return on premium over the swing window (delta·move + theta·days), from the IV distribution.">
                  <span style={{ color: 'var(--text-muted)' }}>EV </span>
                  <b style={{ color: signal.option.evaluation.evPctOfPremium >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {signal.option.evaluation.evPctOfPremium >= 0 ? '+' : ''}{signal.option.evaluation.evPctOfPremium}%
                  </b>
                </span>
              )}
              {signal.option.breakeven != null && (
                <span title="Underlying price at which the option breaks even at expiry (strike ± premium).">
                  <span style={{ color: 'var(--text-muted)' }}>B/E </span>
                  <b>{cs}{Number(signal.option.breakeven).toFixed(2)}</b>
                </span>
              )}
              {signal.option.evaluation.ivRich === 'rich' && (
                <span title="Implied vol is expensive vs the stock's realized vol — you're paying up, and IV crush can hurt even on a correct call." style={{ color: 'var(--amber)', fontWeight: 700 }}>
                  ⚠ IV rich
                </span>
              )}
              {signal.option.evaluation.ivRich === 'cheap' && (
                <span title="Implied vol is cheap vs realized — favorable entry for a long option." style={{ color: 'var(--green)', fontWeight: 700 }}>
                  IV cheap
                </span>
              )}
            </div>
          )}

          <button
            onClick={handleOptionTrade}
            disabled={optionValidating}
            style={{
              marginTop: 9, width: '100%', padding: '8px 0',
              borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)',
              background: optionValidating ? 'var(--bg-elevated)' : 'transparent',
              color: 'var(--accent-soft)', fontSize: 11, fontWeight: 700,
              cursor: optionValidating ? 'wait' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}>
            {optionValidating
              ? <><Loader size={11} className="animate-spin" /> Verifying premium…</>
              : <>Trade option contract</>
            }
          </button>
          {optionError && (
            <div style={{ fontSize: 10, color: 'var(--red-soft)', marginTop: 6, textAlign: 'center' }}>{optionError}</div>
          )}
        </div>
      )}

      {/* AI insight footer — preserved from prior impl */}
      {(aiInsight || aiLoading) && (
        <div style={{
          padding: '8px 11px', borderRadius: 'var(--r-md)',
          background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
          marginBottom: 12,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4,
            fontSize: 9.5, fontWeight: 700, color: 'var(--accent-soft)',
            letterSpacing: '0.04em',
          }}>
            <span title={TERMS.aiReasoning} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'help' }}>
              <Icon name="sparkles" size={11} /> AI READ
            </span>
            {aiInsight?.adjustment != null && (
              <span title={TERMS.aiAdjustment} style={{
                marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                color: aiInsight.adjustment > 0 ? 'var(--green)' : aiInsight.adjustment < 0 ? 'var(--red)' : 'var(--text-muted)',
                cursor: 'help',
              }}>
                {aiInsight.adjustment > 0 ? '+' : ''}{aiInsight.adjustment}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
            {aiLoading && !aiInsight ? 'Analysing…' : (aiInsight?.reasoning || 'No AI commentary for this signal.')}
          </div>
        </div>
      )}

      {/* full-width direction-coloured CTA */}
      <button
        onClick={handleStockTrade}
        disabled={stockValidating}
        style={{
          marginTop: 'auto', width: '100%',
          padding: '11px 0', borderRadius: 'var(--r-md)',
          border: 'none', color: '#fff',
          fontSize: 12.5, fontWeight: 700,
          cursor: stockValidating ? 'wait' : 'pointer',
          fontFamily: 'inherit',
          background: stockValidating ? 'var(--bg-elevated)' : dirGrad,
          boxShadow: stockValidating ? 'none' : `0 4px 14px ${dirGlow}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
        {stockValidating
          ? <><Loader size={12} className="animate-spin" /> Verifying live price…</>
          : <>Trade {signal.direction} @ {cs}{Number(displayPrice || 0).toFixed(2)}</>
        }
      </button>
      {stockError && (
        <div style={{ fontSize: 10, color: 'var(--red-soft)', marginTop: 6, textAlign: 'center' }}>{stockError}</div>
      )}
    </article>
  );
}

// Re-render only when fields that drive the visuals change. Same equality
// check as before — the redesign just changed layout, not the data contract.
function isEqualSignalProps(prev, next) {
  if (prev.cs !== next.cs) return false;
  if (prev.livePrice !== next.livePrice) return false;
  if (prev.aiLoading !== next.aiLoading) return false;
  if (prev.aiInsight?.adjustment !== next.aiInsight?.adjustment) return false;
  if (prev.aiInsight?.reasoning  !== next.aiInsight?.reasoning)  return false;
  const a = prev.signal, b = next.signal;
  if (a === b) return true;
  if (!a || !b) return false;
  return a.ticker === b.ticker
      && a.direction === b.direction
      && a.confidence === b.confidence
      && a.calibratedConfidence === b.calibratedConfidence
      && a.evPct === b.evPct
      && a.trackRecordN === b.trackRecordN
      && a.sector === b.sector
      && a.currentPrice === b.currentPrice
      && a.changePct === b.changePct
      && a.targetPrice === b.targetPrice
      && a.stopLoss === b.stopLoss
      && a.riskReward === b.riskReward
      && a.rsi === b.rsi
      && a.primaryReasonCluster === b.primaryReasonCluster
      && a.score === b.score
      && a._confidenceSource === b._confidenceSource
      && a._backtestResult === b._backtestResult
      && a.option === b.option
      && a.newsSentiment === b.newsSentiment
      && a.dataSource === b.dataSource;
}

const SignalCard = memo(SignalCardImpl, isEqualSignalProps);
export default SignalCard;
